import { useState, useEffect } from 'react';
import { eventService, companyService } from 'services/api';
import SearchableSelect from 'components/SearchableSelect';
import EventModal from './EventModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import styles from './Events.module.css';

export default function Events() {
  const [items,      setItems]      = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [filterComp, setFilterComp] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');

  // Cargar todas las compañías para el filtro
  useEffect(() => {
    companyService.getAll({ limit: 200 }).then(r => setCompanies(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  const load = (cid = filterComp) => {
    if (!cid) { setItems([]); return; }
    setLoading(true);
    eventService.getAll({ company_id: cid })
      .then(r => { setItems(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filterComp]); // eslint-disable-line

  const openNew  = () => { setEditing(null); setModal(true); };
  const openEdit = (e) => { setEditing(e);   setModal(true); };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      await eventService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Evento eliminado' });
      setConfirmDel(null);
      load();
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = (msg) => {
    setAlert({ type: 'success', message: msg });
    setModal(false);
    load();
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('es-CO', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-ticket ${styles.iconoCabecera}`} />Eventos</h1>
          <p>Gestiona los eventos por compañía</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="fas fa-plus" /> Nuevo evento
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <div className={styles.barra}>
          <div className={styles.filtro}>
            <i className={`fas fa-briefcase ${styles.iconoFiltro}`} />
            <SearchableSelect value={filterComp} onChange={(id) => setFilterComp(id)} placeholder="— Selecciona una compañía —" minWidth={240} ariaLabel="Compañía"
              options={companies.map(c => ({ value: c.id, label: c.name }))} />
          </div>
          {filterComp && !loading && (
            <span className={styles.conteo}>{items.length} evento(s)</span>
          )}
        </div>

        {!filterComp ? (
          <div className="empty-state">
            <i className="fas fa-ticket" />
            <p>Selecciona una compañía para ver sus eventos</p>
          </div>
        ) : loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-ticket" />
            <p>Esta compañía no tiene eventos aún</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Compañía</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Días</th>
                  <th>Lugar</th>
                  <th>Estado</th>
                  <th className={styles.colCentro}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.id}>
                    <td>
                      <div className={styles.nombre}>{e.name}</div>
                      {e.description && <div className={styles.descripcion}>{e.description.slice(0, 60)}{e.description.length > 60 ? '...' : ''}</div>}
                    </td>
                    <td className={styles.celda}>{e.company?.name || '—'}</td>
                    <td className={styles.celda}>{fmt(e.start_time)}</td>
                    <td className={styles.celda}>{fmt(e.end_time)}</td>
                    <td className={styles.celda}>{e.duration_days || 1}</td>
                    <td className={styles.celda}>{e.address || '—'}</td>
                    <td>
                      <span className={`badge ${Number(e.status) === 1 ? 'badge-success' : 'badge-secondary'}`}>{Number(e.status) === 1 ? 'Activo' : 'Inactivo'}</span>
                    </td>
                    <td>
                      <div className={`actions ${styles.accionesCentro}`}>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={() => openEdit(e)} title="Editar" aria-label="Editar"><i className="fas fa-pencil" /></button>
                        <button className="btn btn-sm btn-danger btn-icon" onClick={() => setConfirmDel(e)} title="Eliminar" aria-label="Eliminar"><i className="fas fa-trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <EventModal editing={editing} companies={companies} onClose={() => setModal(false)} onSaved={onSaved} />}

      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar evento" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={handleDelete} deleting={deleting} error={delError}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar <span className={styles.confirmaNombre}>{confirmDel.name}</span>?
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}